import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';

const getImageUrl = (image) => {
  if (!image) return null;
  if (/^https?:\/\//i.test(image)) return image;

  const backendBase = (process.env.REACT_APP_BACKEND || '').replace(/\/+$/, '');
  if (!backendBase) return image;

  const normalizedPath = String(image).replace(/^\/+/, '');
  const fullPath = normalizedPath.startsWith('uploads/')
    ? normalizedPath
    : `uploads/${normalizedPath}`;
  return `${backendBase}/${fullPath}`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const LeadershipList = () => {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaders = async () => {
    setLoading(true);
    try {
      const res = await getRequest('leadership?limit=500', getAuthHeaders());
      if (res?.data?.success) {
        setLeaders(res.data.data || []);
      } else {
        toast.error(res?.data?.message || 'Failed to load leaders');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching leaders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaders();
  }, []);

  const handleToggleStatus = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await patchRequest(
        `leadership/${item.id}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      if (res?.data?.success) {
        toast.success(`Leader ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchLeaders();
      } else {
        toast.error(res?.data?.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.heading}"?`)) return;
    try {
      const res = await deleteRequest(`leadership/${item.id}`, getAuthHeaders());
      if (res?.data?.success) {
        toast.success('Leader deleted successfully');
        fetchLeaders();
      } else {
        toast.error(res?.data?.message || 'Delete failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting leader');
    }
  };

  const tableHeaders = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Heading', accessor: 'heading' },
    { Header: 'Title', accessor: 'title' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) =>
        row.original.description ? (
          <span>
            {String(row.original.description).length > 60
              ? `${String(row.original.description).slice(0, 60)}...`
              : row.original.description}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      Header: 'Image',
      accessor: 'image',
      Cell: ({ row }) =>
        getImageUrl(row.original.image) ? (
          <img
            src={getImageUrl(row.original.image)}
            alt={row.original.heading}
            width={100}
            style={{ borderRadius: '4px', objectFit: 'cover' }}
          />
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }) => (
        <span className={`badge ${row.original.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-info mr-2"
            onClick={() => navigate(`/leadership/update/${row.original.id}`)}
          >
            Edit
          </button>
          <button
            className={`btn btn-sm mr-2 ${row.original.status === 'active' ? 'btn-warning' : 'btn-success'}`}
            onClick={() => handleToggleStatus(row.original)}
          >
            {row.original.status === 'active' ? 'Deactivate' : 'Activate'}
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4 d-flex justify-content-between align-items-center">
        <h4 className="font-weight-bold">Leaders</h4>
        <button className="btn btn-success" onClick={() => navigate('/leadership/add')}>
          + Add Leader
        </button>
      </div>

      <div className="mx-4 mt-4">
        {loading ? (
          <div className="text-center mt-5">Loading leaders...</div>
        ) : leaders.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={leaders} />
        ) : (
          <div className="text-center mt-5 text-white">No leaders available</div>
        )}
      </div>
    </div>
  );
};

export default LeadershipList;
